import { useState } from "react";
import { InfoModal } from "./InfoModal";
import { Button } from "./Button";
import { Alert } from "./Alert";
import { useLogDose } from "@/hooks/useDoseEvents";
import type { InjectionSite, TreatmentRow } from "@/lib/database.types";

// Rotation-friendly site set — matches the dose_events CHECK constraint.
const SITES: Array<{ value: InjectionSite; label: string }> = [
  { value: "abdomen_left",  label: "Abdomen · Left" },
  { value: "abdomen_right", label: "Abdomen · Right" },
  { value: "thigh_left",    label: "Thigh · Left" },
  { value: "thigh_right",   label: "Thigh · Right" },
  { value: "arm_left",      label: "Arm · Left" },
  { value: "arm_right",     label: "Arm · Right" },
];

const inputCls =
  "w-full rounded-[10px] border border-fh-border bg-white px-3.5 py-3 text-[15px] text-fh-ink " +
  "placeholder:text-fh-gray-light focus:outline-none focus:border-fh-green focus-visible:ring-2 focus-visible:ring-fh-green/30";
const labelCls = "block text-[13px] font-semibold text-fh-green-dark mb-1.5";

// datetime-local wants "YYYY-MM-DDTHH:mm" in local time.
function localNow(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: string | undefined;
  /** Active treatment — prefills medication and links the dose to the plan. */
  treatment: TreatmentRow | null;
  /** Last logged dose mg, used as the default for the next one. */
  lastDoseMg?: number | null;
}

export function LogDoseDialog({ open, onClose, patientId, treatment, lastDoseMg }: Props) {
  const logDose = useLogDose(patientId);
  const [medication, setMedication] = useState(treatment?.medication ?? "");
  const [doseMg, setDoseMg] = useState(lastDoseMg != null ? String(lastDoseMg) : "");
  const [takenAt, setTakenAt] = useState(localNow());
  const [site, setSite] = useState<InjectionSite | null>(null);
  const [pain, setPain] = useState<number | null>(null);
  const [notes, setNotes] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const mg = Number(doseMg);
    if (!medication.trim() || !Number.isFinite(mg) || mg <= 0) return;
    logDose.mutate(
      {
        medication: medication.trim(),
        dose_mg: mg,
        taken_at: new Date(takenAt).toISOString(),
        injection_site: site,
        pain_level: pain,
        notes: notes.trim() || null,
        treatment_id: treatment?.id ?? null,
      },
      {
        onSuccess: () => {
          setNotes("");
          setPain(null);
          setSite(null);
          setTakenAt(localNow());
          onClose();
        },
      },
    );
  }

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      title="Log a dose"
      subtitle="Record the dose you just took so your levels and history stay accurate."
    >
      <form onSubmit={handleSubmit}>
        {logDose.error && (
          <div className="mb-3">
            <Alert tone="error">
              {logDose.error.message || "Couldn't save your dose. Please try again."}
            </Alert>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label htmlFor="ld-med" className={labelCls}>Medication</label>
            <input
              id="ld-med"
              type="text"
              value={medication}
              onChange={(e) => setMedication(e.target.value)}
              placeholder="e.g. Tirzepatide"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="ld-mg" className={labelCls}>Dose (mg)</label>
            <input
              id="ld-mg"
              type="number"
              inputMode="decimal"
              min="0.05"
              step="0.05"
              value={doseMg}
              onChange={(e) => setDoseMg(e.target.value)}
              placeholder="e.g. 2.5"
              required
              className={inputCls}
            />
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="ld-when" className={labelCls}>When</label>
          <input
            id="ld-when"
            type="datetime-local"
            value={takenAt}
            onChange={(e) => setTakenAt(e.target.value)}
            required
            className={inputCls}
          />
        </div>

        <div className="mb-4">
          <span className={labelCls}>Injection site (rotate each week)</span>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SITES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setSite(site === s.value ? null : s.value)}
                aria-pressed={site === s.value}
                className={[
                  "rounded-[9px] border px-2 py-2 text-[12.5px] font-semibold transition",
                  site === s.value
                    ? "border-fh-green bg-fh-sage text-fh-green-dark"
                    : "border-fh-border bg-white text-fh-gray hover:border-fh-green-light",
                ].join(" ")}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <span className={labelCls}>Injection pain (0 = none, 5 = severe)</span>
          <div className="flex gap-2">
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPain(pain === n ? null : n)}
                aria-pressed={pain === n}
                className={[
                  "w-10 h-10 rounded-full border text-[14px] font-semibold transition",
                  pain === n
                    ? "border-fh-green bg-fh-green text-white"
                    : "border-fh-border bg-white text-fh-gray hover:border-fh-green-light",
                ].join(" ")}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <label htmlFor="ld-notes" className={labelCls}>Notes (optional)</label>
          <textarea
            id="ld-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything you noticed — side effects, timing, etc."
            className={inputCls + " resize-y"}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" type="submit" loading={logDose.isPending}>
            Save dose
          </Button>
        </div>
      </form>
    </InfoModal>
  );
}
